import { Children, isValidElement, useCallback, useMemo, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { isAthleteCollegeOnList } from "../../hooks/athleteCollegeListCache";
import { useAthleteCollegeList } from "../../hooks/useAthleteCollegeList";
import {
    attachCollegeContactFromAssist,
    findAndAddCollegeToAthleteList,
} from "../../utils/addCollegeFromAssist";
import { addContactFromAssist } from "../../utils/addContactFromAssist";
import { parseListedCollegeFromText, type ListedCollege } from "../../utils/extractListedColleges";
import {
    extractAllAssistContacts,
    extractListedContacts,
    type ListedContact,
} from "../../utils/extractListedContacts";
import { fetchAssistCollegePage } from "../../services/polarisService";
import { ASSIST_LIST_PAGE_SIZE, pageCount, pageSlice } from "./assistListPagination";

const COLLEGE_STATUS: Record<string, string> = {
    added: "On your college list.",
    already: "On your college list.",
    looking: "Looking up a staff contact…",
    saved: "On your college list. A staff contact was saved.",
    none: "On your college list. No staff contact found.",
    not_found: "Not in our college catalog.",
    error: "Something went wrong. Please try again.",
};

const ON_LIST_LABEL = "On your college list.";
const NOT_IN_CATALOG_LABEL = COLLEGE_STATUS.not_found;

const CONTACT_STATUS: Record<string, string> = {
    added: "Added to this college's contacts.",
    already: "Already saved for this college.",
    not_on_list: "Add this college to your list first, then try again.",
    not_found: "We couldn't save that contact. Try My contacts on College Planning.",
    invalid: "That contact is missing a valid email or phone.",
    error: "Something went wrong. Please try again.",
};

function textFromNode(node: ReactNode): string {
    if (node == null || typeof node === "boolean") {
        return "";
    }
    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(textFromNode).join("");
    }
    if (isValidElement<{ children?: ReactNode }>(node)) {
        return textFromNode(node.props.children);
    }
    return "";
}

function locationLabel(college: ListedCollege): string {
    const parts = [college.city, college.state].filter(Boolean);
    return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

export default function AssistAnswerBody({
    markdown,
    canAdd,
    username,
    collegeHint,
    userQuery,
}: {
    markdown: string;
    canAdd: boolean;
    username: string;
    collegeHint?: string;
    userQuery?: string;
}) {
    const {
        names: athleteCollegeNames,
        remember: rememberCollegeOnList,
        ready: collegeListReady,
    } = useAthleteCollegeList(username, canAdd);
    const answerContacts = useMemo(
        () => extractAllAssistContacts(markdown, 50, collegeHint),
        [markdown, collegeHint],
    );
    const listedContacts = useMemo(
        () => extractListedContacts(markdown, 50, collegeHint),
        [markdown, collegeHint],
    );
    const proseContacts = useMemo(
        () =>
            answerContacts.filter(
                (contact) =>
                    !listedContacts.some(
                        (listed) =>
                            listed.fullName === contact.fullName &&
                            listed.collegeName === contact.collegeName &&
                            listed.phone === contact.phone &&
                            listed.email === contact.email,
                    ),
            ),
        [answerContacts, listedContacts],
    );
    const [extraColleges, setExtraColleges] = useState<ListedCollege[]>([]);
    const [collegePage, setCollegePage] = useState(0);
    const [contactPage, setContactPage] = useState(0);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [status, setStatus] = useState<Record<string, string>>({});
    const [loadingMore, setLoadingMore] = useState(false);
    const [moreTotal, setMoreTotal] = useState<number | null>(null);
    const [moreExhausted, setMoreExhausted] = useState(false);

    const matchContact = useCallback(
        (text: string): ListedContact | null => {
            return (
                answerContacts.find((contact) => text.includes(contact.fullName)) ?? null
            );
        },
        [answerContacts],
    );

    const onAddCollege = useCallback(
        async (college: ListedCollege) => {
            setBusyKey(college.name);
            const result = await findAndAddCollegeToAthleteList(username, college);
            if (result.status === "added" || result.status === "already") {
                rememberCollegeOnList(college.name);
                if (result.collegeId != null) {
                    setStatus((prev) => ({ ...prev, [college.name]: COLLEGE_STATUS.looking }));
                    const attached = await attachCollegeContactFromAssist(
                        username,
                        college,
                        result.collegeId,
                        markdown,
                        userQuery,
                    );
                    setStatus((prev) => ({ ...prev, [college.name]: COLLEGE_STATUS[attached] }));
                } else {
                    setStatus((prev) => ({ ...prev, [college.name]: COLLEGE_STATUS[result.status] }));
                }
                setBusyKey(null);
                return;
            }
            setStatus((prev) => ({ ...prev, [college.name]: COLLEGE_STATUS[result.status] }));
            setBusyKey(null);
        },
        [markdown, rememberCollegeOnList, userQuery, username],
    );

    const onAddContact = useCallback(
        async (contact: ListedContact) => {
            const key = `${contact.fullName}|${contact.collegeName}`;
            setBusyKey(key);
            const result = await addContactFromAssist(username, contact);
            setStatus((prev) => ({ ...prev, [key]: CONTACT_STATUS[result] }));
            setBusyKey(null);
        },
        [username],
    );

    const markdownComponents = useMemo(
        () => ({
            ul: ({ children }: { children?: ReactNode }) => {
                const items = Children.toArray(children).filter(
                    (child) => textFromNode(child).trim().length > 0,
                );
                const classified = items.map((child) => {
                    const text = textFromNode(child);
                    return {
                        child,
                        college: parseListedCollegeFromText(text),
                        contact: matchContact(text),
                    };
                });
                const colleges = classified
                    .map((item) => item.college)
                    .filter((college): college is ListedCollege => college != null);
                const contacts = classified
                    .map((item) => item.contact)
                    .filter((contact): contact is ListedContact => contact != null);
                if (isMostlyCollegeList(classified, colleges)) {
                    const merged = mergeColleges(colleges, extraColleges);
                    return (
                        <PaginatedCollegeList
                            colleges={merged}
                            page={collegePage}
                            onPage={setCollegePage}
                            canAdd={canAdd}
                            busyKey={busyKey}
                            status={status}
                            athleteCollegeNames={athleteCollegeNames}
                            collegeListReady={collegeListReady}
                            onAdd={onAddCollege}
                            userQuery={userQuery}
                            loadingMore={loadingMore}
                            moreTotal={moreTotal}
                            moreExhausted={moreExhausted}
                            onShowMore={
                                userQuery
                                    ? async () => {
                                          setLoadingMore(true);
                                          try {
                                              const alreadyShown = merged.length;
                                              const page = await fetchAssistCollegePage(
                                                  userQuery,
                                                  collegeHint,
                                                  0,
                                                  ASSIST_LIST_PAGE_SIZE,
                                                  merged.map((college) => college.name),
                                              );
                                              setMoreTotal(page.total);
                                              if (page.colleges.length === 0) {
                                                  setMoreExhausted(true);
                                              } else {
                                                  setExtraColleges((prev) =>
                                                      mergeColleges(
                                                          prev,
                                                          page.colleges.map((item) => ({
                                                              name: item.name,
                                                              city: item.city ?? undefined,
                                                              state: item.state ?? undefined,
                                                          })),
                                                      ),
                                                  );
                                                  setCollegePage(
                                                      Math.floor(alreadyShown / ASSIST_LIST_PAGE_SIZE),
                                                  );
                                              }
                                          } catch {
                                              setMoreExhausted(true);
                                          }
                                          setLoadingMore(false);
                                      }
                                    : undefined
                            }
                        />
                    );
                }
                if (contacts.length > 0 && contacts.length === classified.length) {
                    return (
                        <PaginatedContactList
                            contacts={contacts}
                            page={contactPage}
                            onPage={setContactPage}
                            canAdd={canAdd}
                            busyKey={busyKey}
                            status={status}
                            onAdd={onAddContact}
                        />
                    );
                }
                return (
                    <ul>
                        {classified.map((item, index) => (
                            <InteractiveListItem
                                key={index}
                                college={item.college}
                                contact={item.contact}
                                fallback={item.child}
                                canAdd={canAdd}
                                busyKey={busyKey}
                                status={status}
                                athleteCollegeNames={athleteCollegeNames}
                                collegeListReady={collegeListReady}
                                onAddCollege={onAddCollege}
                                onAddContact={onAddContact}
                            />
                        ))}
                    </ul>
                );
            },
        }),
        [
            athleteCollegeNames,
            collegeListReady,
            busyKey,
            canAdd,
            collegeHint,
            collegePage,
            contactPage,
            extraColleges,
            loadingMore,
            matchContact,
            moreExhausted,
            moreTotal,
            onAddCollege,
            onAddContact,
            status,
            userQuery,
        ],
    );

    return (
        <>
            <ReactMarkdown components={markdownComponents}>{markdown}</ReactMarkdown>
            {canAdd && proseContacts.length > 0 ? (
                <PaginatedContactList
                    contacts={proseContacts}
                    page={contactPage}
                    onPage={setContactPage}
                    canAdd={canAdd}
                    busyKey={busyKey}
                    status={status}
                    onAdd={onAddContact}
                />
            ) : null}
        </>
    );
}

function isMostlyCollegeList(
    classified: { college: ListedCollege | null; contact: ListedContact | null }[],
    colleges: ListedCollege[],
): boolean {
    if (colleges.length === 0 || classified.length === 0) {
        return false;
    }
    if (classified.some((item) => item.contact != null && item.college == null)) {
        return false;
    }
    if (colleges.length === classified.length) {
        return true;
    }
    return colleges.length >= 3 && colleges.length * 2 >= classified.length;
}

function mergeColleges(base: ListedCollege[], extra: ListedCollege[]): ListedCollege[] {
    const seen = new Set(base.map((college) => college.name.toLowerCase()));
    const merged = [...base];
    for (const college of extra) {
        const key = college.name.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        merged.push(college);
    }
    return merged;
}

function InteractiveListItem({
    college,
    contact,
    fallback,
    canAdd,
    busyKey,
    status,
    athleteCollegeNames,
    collegeListReady,
    onAddCollege,
    onAddContact,
}: {
    college: ListedCollege | null;
    contact: ListedContact | null;
    fallback: ReactNode;
    canAdd: boolean;
    busyKey: string | null;
    status: Record<string, string>;
    athleteCollegeNames: string[];
    collegeListReady: boolean;
    onAddCollege: (college: ListedCollege) => void;
    onAddContact: (contact: ListedContact) => void;
}) {
    if (college && canAdd) {
        return (
            <li>
                <CollegeRow
                    college={college}
                    canAdd={canAdd}
                    busy={busyKey === college.name}
                    status={status[college.name]}
                    onList={isAthleteCollegeOnList(college.name, athleteCollegeNames)}
                    listReady={collegeListReady}
                    onAdd={onAddCollege}
                />
            </li>
        );
    }
    if (contact && canAdd) {
        const key = `${contact.fullName}|${contact.collegeName}`;
        return (
            <li>
                <ContactRow
                    contact={contact}
                    canAdd={canAdd}
                    busy={busyKey === key}
                    status={status[key]}
                    onAdd={onAddContact}
                />
            </li>
        );
    }
    return <>{fallback}</>;
}

function PaginatedCollegeList({
    colleges,
    page,
    onPage,
    canAdd,
    busyKey,
    status,
    athleteCollegeNames,
    collegeListReady,
    onAdd,
    userQuery,
    loadingMore,
    moreTotal,
    moreExhausted,
    onShowMore,
}: {
    colleges: ListedCollege[];
    page: number;
    onPage: (page: number) => void;
    canAdd: boolean;
    busyKey: string | null;
    status: Record<string, string>;
    athleteCollegeNames: string[];
    collegeListReady: boolean;
    onAdd: (college: ListedCollege) => void;
    userQuery?: string;
    loadingMore: boolean;
    moreTotal: number | null;
    moreExhausted: boolean;
    onShowMore?: () => void;
}) {
    const totalPages = pageCount(colleges.length);
    const safePage = Math.min(page, totalPages - 1);
    const visible = pageSlice(colleges, safePage);
    const start = safePage * ASSIST_LIST_PAGE_SIZE + 1;
    const end = Math.min(colleges.length, start + visible.length - 1);
    const onLastPage = safePage >= totalPages - 1;
    const canShowMore =
        Boolean(onShowMore && userQuery && onLastPage && !moreExhausted && colleges.length >= ASSIST_LIST_PAGE_SIZE);
    return (
        <div className="assist-paginated-list">
            <ul className="assist-inline-list">
                {visible.map((college) => (
                    <li key={college.name}>
                        <CollegeRow
                            college={college}
                            canAdd={canAdd}
                            busy={busyKey === college.name}
                            status={status[college.name]}
                            onList={isAthleteCollegeOnList(college.name, athleteCollegeNames)}
                            listReady={collegeListReady}
                            onAdd={onAdd}
                        />
                    </li>
                ))}
            </ul>
            {colleges.length > ASSIST_LIST_PAGE_SIZE || canShowMore ? (
                <nav className="assist-list-pager" aria-label="College list pages">
                    <p className="assist-list-pager-range">
                        {start}–{end} of {moreTotal ?? colleges.length}
                    </p>
                    <div className="assist-list-pager-actions">
                        <button
                            type="button"
                            className="assist-pager-btn"
                            disabled={safePage <= 0}
                            onClick={() => onPage(safePage - 1)}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            className="assist-pager-btn"
                            disabled={onLastPage && !canShowMore}
                            onClick={() => {
                                if (!onLastPage) {
                                    onPage(safePage + 1);
                                    return;
                                }
                                if (canShowMore && onShowMore) {
                                    void onShowMore();
                                }
                            }}
                        >
                            {onLastPage && canShowMore ? (loadingMore ? "Loading…" : "Show more") : "Next"}
                        </button>
                    </div>
                </nav>
            ) : null}
        </div>
    );
}

function PaginatedContactList({
    contacts,
    page,
    onPage,
    canAdd,
    busyKey,
    status,
    onAdd,
}: {
    contacts: ListedContact[];
    page: number;
    onPage: (page: number) => void;
    canAdd: boolean;
    busyKey: string | null;
    status: Record<string, string>;
    onAdd: (contact: ListedContact) => void;
}) {
    const totalPages = pageCount(contacts.length);
    const safePage = Math.min(page, totalPages - 1);
    const visible = pageSlice(contacts, safePage);
    const start = safePage * ASSIST_LIST_PAGE_SIZE + 1;
    const end = Math.min(contacts.length, start + visible.length - 1);
    return (
        <div className="assist-paginated-list">
            <ul className="assist-inline-list">
                {visible.map((contact) => {
                    const key = `${contact.fullName}|${contact.collegeName}`;
                    return (
                        <li key={key}>
                            <ContactRow
                                contact={contact}
                                canAdd={canAdd}
                                busy={busyKey === key}
                                status={status[key]}
                                onAdd={onAdd}
                            />
                        </li>
                    );
                })}
            </ul>
            {contacts.length > ASSIST_LIST_PAGE_SIZE ? (
                <nav className="assist-list-pager" aria-label="Contact list pages">
                    <p className="assist-list-pager-range">
                        {start}–{end} of {contacts.length}
                    </p>
                    <div className="assist-list-pager-actions">
                        <button
                            type="button"
                            className="assist-pager-btn"
                            disabled={safePage <= 0}
                            onClick={() => onPage(safePage - 1)}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            className="assist-pager-btn"
                            disabled={safePage >= totalPages - 1}
                            onClick={() => onPage(safePage + 1)}
                        >
                            Next
                        </button>
                    </div>
                </nav>
            ) : null}
        </div>
    );
}

function CollegeRow({
    college,
    canAdd,
    busy,
    status,
    onList,
    listReady,
    onAdd,
}: {
    college: ListedCollege;
    canAdd: boolean;
    busy: boolean;
    status?: string;
    onList: boolean;
    listReady: boolean;
    onAdd: (college: ListedCollege) => void;
}) {
    const label = status ?? (onList ? ON_LIST_LABEL : undefined);
    const showButton =
        canAdd && listReady && !onList && !busy && (status == null || status === COLLEGE_STATUS.error);
    const muted =
        status === NOT_IN_CATALOG_LABEL
        || status === ON_LIST_LABEL
        || (onList && (status == null || status === COLLEGE_STATUS.added || status === COLLEGE_STATUS.already));
    return (
        <div className="assist-college-action-row">
            <span className="assist-college-action-name">
                {college.name}
                {locationLabel(college)}
            </span>
            {showButton ? (
                <button
                    type="button"
                    className="assist-add-college-btn"
                    disabled={busy}
                    onClick={() => onAdd(college)}
                >
                    {busy ? "Adding…" : "Add to college list"}
                </button>
            ) : null}
            {busy && !label ? (
                <span className="assist-college-action-status" role="status">
                    Adding…
                </span>
            ) : null}
            {label ? (
                <span
                    className={`assist-college-action-status${muted ? " is-muted" : ""}`}
                    role="status"
                >
                    {label}
                </span>
            ) : null}
        </div>
    );
}

function ContactRow({
    contact,
    canAdd,
    busy,
    status,
    onAdd,
}: {
    contact: ListedContact;
    canAdd: boolean;
    busy: boolean;
    status?: string;
    onAdd: (contact: ListedContact) => void;
}) {
    return (
        <div className="assist-college-action-row">
            <span className="assist-college-action-name">
                {contact.fullName}
                {contact.title ? ` · ${contact.title}` : ""}
            </span>
            {canAdd && !status ? (
                <button
                    type="button"
                    className="assist-add-college-btn"
                    disabled={busy}
                    onClick={() => onAdd(contact)}
                >
                    {busy ? "Adding…" : "Add contact"}
                </button>
            ) : null}
            {status ? (
                <span className="assist-college-action-status" role="status">
                    {status}
                </span>
            ) : null}
        </div>
    );
}
